<!--
  @fileoverview Feedbacks Component - Emoji/Quick Reply Feedback (Vue 3)

  Renders feedback collection UI with emoji quick replies, optional comment
  input fields, and thank you confirmation. Used for gathering user feedback
  on bot responses.

  @component MessageFeedbacks
  @displayName Feedback Quick Replies

  Features:
  - Quick reply emoji buttons for simple feedback
  - Popover input for text comments
  - Input with comments for combined feedback
  - Thank you confirmation with re-rate option
  - Customizable labels via settings
  - Language-based label support

  @example
  <MessageFeedbacks
    :message="messageWithFeedback"
    @sendPostback="handleFeedbackSubmit"
  />

  @vue3migration
  - Migrated to script setup syntax
  - Replaced mapGetters with useMainStore()
  - Replaced this.$store.getters.settings with store.settings
  - Converted mixin to composable pattern
  - Added proper TypeScript-style prop definitions
-->
<template>
  <el-row v-if="submitted" type="flex" justify="end" class="emoji-feedback !mt-[10px] !mb-0">
    <span class="!text-[10px] break-words">
      {{ thanksLabel }}
      <el-button link @click="submitted = false">
        <span v-show="isShowReRate" class="!text-[10px]">{{ reRateLabel }}</span>
      </el-button>
    </span>
  </el-row>
  <el-row
    v-else-if="!submitted"
    type="flex"
    justify="end"
    align="middle"
    class="emoji-feedback !mt-[10px] !mb-0"
  >
    <el-col>
      <p
        v-show="isShowQuestionLabel && questionLabel"
        class="question-label"
        v-html="questionLabel"
      ></p>
    </el-col>
    <el-button
      v-for="(qr, i) in quckReplies"
      :key="`pop-${i}`"
      size="small"
      link
      class="keyreply-bounce !p-[2px] !m-0"
      @click="submitFeedback(qr)"
    >
      {{ qr.text }}
    </el-button>

    <!-- Input with comments -->
    <el-popover
      v-for="(qric, i) in quickRepliesInputWithComments"
      :key="`popinputcomment-${i}`"
      placement="bottom"
      trigger="manual"
      v-model:visible="qric.visible"
      width="fit-content"
    >
      <div class="feedback-font text-xs mb-[5px]">
        {{ helpUsImproveBetter }}
      </div>
      <textarea
        v-model="qric.data.comments"
        maxlength="140"
        class="feedback-font h-[20px] w-[90%]"
      ></textarea>
      <div class="text-right m-0">
        <!-- @vue3migration: Removed size="small" - Element Plus default size is equivalent -->
        <el-button type="primary" size="small" @click="submitFeedback(qric)">
          {{ qric.data.comments ? submitButton : submitWithoutFeedback }}
        </el-button>
      </div>
      <template #reference>
        <el-button class="keyreply-bounce !p-[2px] !m-0" link @click="togglePopoverInput(i)">{{
          qric.text
        }}</el-button>
      </template>
    </el-popover>

    <!-- Comments -->
    <el-popover
      v-if="quickRepliesComments.length"
      placement="bottom"
      trigger="manual"
      v-model:visible="quickRepliesComments[0].visible"
      width="fit-content"
    >
      <div class="feedback-font text-xs mb-[5px]">
        {{ inputYourFeedbackLabel }}
      </div>
      <textarea
        v-model="quickRepliesComments[0].data.value"
        maxlength="140"
        class="feedback-font"
      ></textarea>
      <div class="text-right m-0">
        <!-- @vue3migration: Removed size="small" - Element Plus default size is equivalent -->
        <el-button type="primary" size="small" @click="submitFeedback(quickRepliesComments[0])">
          {{ submitButton }}
        </el-button>
      </div>
      <template #reference>
        <el-button
          size="small"
          class="keyreply-bounce !p-[2px] !m-0"
          link
          @click="togglePopoverComment()"
        >
          {{ quickRepliesComments[0].text }}</el-button
        >
      </template>
    </el-popover>
  </el-row>
</template>

<script setup>
/**
 * @module components/ChatWindow/Message/Feedbacks
 * @description Feedback collection component with emoji quick replies (Vue 3 Composition API)
 */
import { ref, computed, onMounted } from "vue";
import _ from "lodash-es";
import { useMainStore } from "@/stores/main";
import { parseLanguageBasedLabel } from "@/helpers/langLabels";

/**
 * Component props with proper types
 */
const props = defineProps({
  /**
   * Message object containing feedback quick replies data
   */
  message: {
    type: Object,
    required: true,
  },
});

/**
 * Component emits
 * @event sendPostback - Emits feedback to parent for processing
 */
const emit = defineEmits(["sendPostback"]);

/**
 * Pinia store instance
 */
const store = useMainStore();

/**
 * Reactive state
 */
const quckReplies = ref([]);
const quickRepliesComments = ref([
  {
    visible: false,
    data: {
      value: "",
    },
  },
]);
const quickRepliesInputWithComments = ref([
  {
    visible: false,
    data: {
      comments: "",
    },
  },
]);
const submitted = ref(false);

/**
 * Get messages from store
 */
const messages = computed(() => store.messages);

/**
 * Access webchat settings from Pinia store
 */
const settings = computed(() => store.settings);

/**
 * Find the last message with feedback quick replies
 * @returns {string} ID of the last message with feedback
 */
const lastMessageWithFeedback = computed(() => {
  const sorted = _.orderBy(messages.value, ["timestamp"], ["asc"]);
  const last = _.findLast(sorted, (msg) => msg.data.learningQuickReplies?.length);
  return last.id;
});

/**
 * Check if question label should be shown
 * @returns {boolean} True if label should be shown
 */
const isShowQuestionLabel = computed(() => {
  const label = _.get(settings.value, "feedbackCollectionShowLabel", false);
  return label;
});

/**
 * Check if re-rate option should be shown
 * @returns {boolean} True if re-rate is enabled
 */
const isShowReRate = computed(() => {
  const showReRate = !_.get(settings.value, "feedbackCollectionDisableReRate", false);
  return showReRate;
});

/**
 * Get question label text
 * @returns {string} Question label text
 */
const questionLabel = computed(() => {
  return getLabelFeedbacks("customLearningQuestionLabel", "Did this answer your question?");
});

/**
 * Get thanks label text
 * @returns {string} Thanks label text
 */
const thanksLabel = computed(() => {
  return getLabelFeedbacks("customLearningThanksLabel", "Thanks for your feedback");
});

/**
 * Get help us improve better label text
 * @returns {string} Help label text
 */
const helpUsImproveBetter = computed(() => {
  return getLabelFeedbacks("helpUsImproveBetterLabel", "Help us improve better");
});

/**
 * Get submit without feedback label text
 * @returns {string} Submit without feedback label text
 */
const submitWithoutFeedback = computed(() => {
  return getLabelFeedbacks("submitWithoutFeedbackLabel", "Submit without feedback");
});

/**
 * Get submit button label text
 * @returns {string} Submit button label text
 */
const submitButton = computed(() => {
  return getLabelFeedbacks("submitLabel", "Submit");
});

/**
 * Get re-rate label text
 * @returns {string} Re-rate label text
 */
const reRateLabel = computed(() => {
  return getLabelFeedbacks("reRateLabel", "Re-rate");
});

/**
 * Get input your feedback label text
 * @returns {string} Input feedback label text
 */
const inputYourFeedbackLabel = computed(() => {
  return getLabelFeedbacks("inputYourFeedbackLabel", "Input your feedback");
});

/**
 * Get label for feedback based on language settings
 * (Migrated from mixin)
 * @param {string} type - Label type key
 * @param {string} text - Default text
 * @returns {string} Localized label text
 */
function getLabelFeedbacks(type, text) {
  const lang = _.get(props.message, "data.meta.lang", "default");
  const resultWithCurrentLanguage = parseLanguageBasedLabel(
    settings.value.languageBasedLabels,
    type,
    localStorage.getItem("currentLanguage"),
    text
  );
  if (resultWithCurrentLanguage) return resultWithCurrentLanguage;
  return parseLanguageBasedLabel(settings.value.languageBasedLabels, type, lang, text);
}

/**
 * Toggle comment popover visibility
 */
function togglePopoverComment() {
  if (!quickRepliesComments.value[0].visible) {
    quickRepliesInputWithComments.value = quickRepliesInputWithComments.value.map((qr) => ({
      ...qr,
      visible: false,
    }));
  }
  quickRepliesComments.value[0].visible = !quickRepliesComments.value[0].visible;
}

/**
 * Toggle input with comments popover visibility
 * @param {number} i - Index of popover to toggle
 */
function togglePopoverInput(i) {
  if (!quickRepliesInputWithComments.value[i].visible) {
    handleQuickRepliesCommentVisibility();
    quickRepliesInputWithComments.value = quickRepliesInputWithComments.value.map((qr, idx) => {
      return idx === i
        ? qr
        : {
            ...qr,
            visible: false,
          };
    });
  }
  quickRepliesInputWithComments.value[i].visible = !quickRepliesInputWithComments.value[i].visible;
}

/**
 * Get notify box offset based on mode
 * @returns {number} Offset value
 */
function getNotifyBoxOffset() {
  return store.isMobileMode ? 40 : 600;
}

/**
 * Hide all quick replies comment popovers
 */
function handleQuickRepliesCommentVisibility() {
  if (quickRepliesComments.value.length) {
    quickRepliesComments.value[0].visible = false;
  }
}

/**
 * Submit feedback
 * @param {Object} feedback - Feedback object with value and options
 */
function submitFeedback(feedback) {
  const value = feedback.data.value;
  if (!value) {
    return;
  }

  const options = feedback.options || {};
  const queryType = feedback.options?.queryType || "faq";
  const contentNodeText = _.get(props.message.data.content[0], "text", "");
  const mutatedButton = {
    ...feedback,
    data: {
      value,
      options: {
        ...options,
        feedbackCollection: true,
        queryType,
        // if there is no comment will be undefined
        comments: feedback.data.comments,
        contentNodeText,
      },
    },
    // omit the visible state to sent
    visible: undefined,
  };
  emit("sendPostback", mutatedButton);
  submitted.value = true;
  handleQuickRepliesCommentVisibility();
  quickRepliesInputWithComments.value = quickRepliesInputWithComments.value.map((qr) => ({
    ...qr,
    visible: false,
  }));
}

/**
 * Initialize quick replies from message data on mount
 */
onMounted(() => {
  quckReplies.value = _.cloneDeep(props.message.data.learningQuickReplies);

  // comments
  const quickRepliesCommentsData = _.remove(quckReplies.value, (qr) =>
    _.get(qr, "data.type", ["input"]).includes("comment")
  ).map((qr) => ({
    ...qr,
    visible: false,
  }));
  if (quickRepliesCommentsData.length) {
    quickRepliesComments.value = quickRepliesCommentsData;
  }

  // input with comment
  const quickRepliesInputWithCommentsData = _.remove(quickRepliesCommentsData, (qr) =>
    _.get(qr, "data.type", ["input"]).includes("input")
  ).map((qr) => ({
    ...qr,
    data: {
      ...qr.data,
      comments: "",
    },
    visible: false,
  }));
  if (quickRepliesInputWithCommentsData.length) {
    quickRepliesInputWithComments.value = quickRepliesInputWithCommentsData;
  }
});
</script>

<style scoped lang="scss">
.keyreply-bounce {
  animation-duration: 1s;
  transform-origin: bottom;
}

.keyreply-bounce:hover {
  animation-name: keyreply-bounce;
  animation-timing-function: ease;
}

.feedback-font {
  font-family: var(--keyreply-font-family);
}

/* Textarea styles moved to non-scoped block with .el-popover prefix */

.question-label {
  margin-right: 5px;
  float: right;
  font-size: 10px !important;
  word-break: break-word;
}

@keyframes keyreply-bounce {
  0% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-5px);
  }
  100% {
    transform: translateY(0);
  }
}
</style>

<style>
.emoji-feedback button {
  min-width: auto;
}

/* Popover textarea styling - must be non-scoped for teleported content */
.el-popover textarea {
  padding: 5px;
  border: 3px solid #cccccc;
  margin-bottom: 10px;
  font-size: 12px;
}
</style>
